/**
 * FlashBook level matching — running the whole-book loudness pass.
 *
 * The measurement itself lives in `#/lib/flashbookLoudness` and is pure. This is
 * the part that touches the world: which clips a book has, decoding them,
 * rendering the ones that change, and saying honestly which ones it could not.
 *
 * **Memory is the constraint that shapes it.** A three-minute stereo take is
 * around 60 MB of PCM once decoded, so a twenty-page book held in memory at once
 * is a gigabyte and a crashed tab on exactly the books the feature is most
 * useful for. So the pass runs in two sweeps and holds one clip at a time:
 *
 *  1. **measure** every clip — decode, measure, drop the samples;
 *  2. **plan** (pure, in the loudness module) from those numbers;
 *  3. **apply** only the clips that change — decode each again, multiply, write.
 *
 * The second decode costs almost nothing on either platform: a recording is
 * already a local blob or file, and an asset URL on a device goes through the
 * engine's idempotent download, which finds the file the first sweep left.
 *
 * Nothing here decides what "matched" means — that is the planner's job, and it
 * is testable without a decoder. This module is deliberately thin: it moves bytes
 * and reports what happened.
 */
import {
  type AudioOpenError,
  audioProblemOf,
  decodeAudioForEditing,
  renderedClipUrl,
  renderLevelledAudio,
} from '#/lib/flashbookAudioEditor'
import {clipSourceFor} from '#/lib/flashbookClips'
import {
  type LevelMatchPlan,
  type LoudnessMeasurement,
  measureLoudness,
} from '#/lib/flashbookLoudness'

/** The clip id the cover's spoken introduction gets, so it is addressable. */
export const COVER_CLIP_ID = 'cover'

/** The `kind` of the cover's own reading — it is not a block type. */
export const COVER_CLIP_KIND = 'cover'

/**
 * A reading, as the pass sees it.
 *
 * `page` is `null` for the cover's introduction rather than a page index, so a
 * caller never has to guess which numbered page the cover is.
 *
 * `kind` is carried because a *page* does not identify a reading. A rich reading
 * page holds three or four of them — a quote, a callout, a caption — and a list
 * that said "Page 3" three times with three different numbers beside it would be
 * asking the creator to guess which of their recordings is which.
 */
export type ReadingClip = {
  /** A block's id, or `cover`. */
  id: string
  /** 0-based page index, or `null` for the cover. */
  page: number | null
  /** What it is: a rich block's presentation, a block's type, or `cover`. */
  kind: string
  /** Where the bytes are — a recording, a render, or an uploaded asset. */
  url: string
}

/** A clip the pass could not open, named rather than silently dropped. */
export type ReadingClipFailure = {
  clip: ReadingClip
  problem: AudioOpenError
}

/** A clip that has been measured, with the number the planner needs. */
export type MeasuredReadingClip = {
  clip: ReadingClip
  measurement: LoudnessMeasurement
}

/** A clip that has been rewritten, and the URL it now lives at. */
export type LevelledReadingClip = {
  clip: ReadingClip
  /** The new clip — a blob or file until the studio makes it durable. */
  url: string
  /** How much it moved, in dB. */
  gainDb: number
}

/** Called after each clip, so a slow book can say how far it has got. */
export type LevelProgress = (done: number, total: number) => void

/**
 * Every clip in a book that is read aloud, in the order a reader meets them.
 *
 * The cover comes first because it is the first thing a learner hears: a spoken
 * introduction that is several decibels adrift sets the expectation for the whole
 * book before a single page turns.
 *
 * Written against the shape it needs rather than the full model, so the pass can
 * be exercised without a book.
 */
export function readingClipsOf(
  pages: readonly {
    blocks: readonly {
      id: string
      type?: string
      contentKind?: string | null
      readingAudioUrl?: string
    }[]
  }[],
  coverAudioUri?: string,
): ReadingClip[] {
  const clips: ReadingClip[] = []
  if (coverAudioUri) {
    clips.push({
      id: COVER_CLIP_ID,
      page: null,
      kind: COVER_CLIP_KIND,
      url: coverAudioUri,
    })
  }
  pages.forEach((page, index) => {
    for (const block of page.blocks) {
      if (block.readingAudioUrl) {
        clips.push({
          id: block.id,
          page: index,
          // A rich block says what it is (`quote`); anything else is its own
          // type, which is the same word the studio's block header uses.
          kind: block.contentKind ?? block.type ?? 'content',
          url: block.readingAudioUrl,
        })
      }
    }
  })
  return clips
}

/**
 * The identity of one clip's level match.
 *
 * Scoped to the clip rather than derived from its URL, so re-running the pass on
 * a book that has already been matched overwrites the same rendered file instead
 * of leaving a trail of megabytes behind — and so two blocks holding the same
 * recording do not collide, because each block's id is in its own key.
 */
export function levelKeyFor(clip: ReadingClip, gainDb: number): string {
  return `level-${clip.id}-${Math.round(gainDb * 10)}`
}

/**
 * Measures every clip, one at a time.
 *
 * Sequential on purpose. Decoding a book in parallel holds every byte of every
 * take at once, and browsers cap how many audio contexts a page may open — so the
 * parallel version would be faster right up until it failed on the larger books.
 * A clip that will not open is *recorded*, not thrown: one unreadable page must
 * not cost the creator the other nineteen.
 */
export async function measureReadingClips(
  clips: readonly ReadingClip[],
  onProgress?: LevelProgress,
): Promise<{measured: MeasuredReadingClip[]; failures: ReadingClipFailure[]}> {
  const measured: MeasuredReadingClip[] = []
  const failures: ReadingClipFailure[] = []
  let done = 0
  for (const clip of clips) {
    try {
      const buffer = await decodeAudioForEditing(clipSourceFor(clip.url))
      measured.push({clip, measurement: measureLoudness(buffer)})
    } catch (error) {
      failures.push({clip, problem: audioProblemOf(error)})
    }
    done += 1
    onProgress?.(done, clips.length)
  }
  return {measured, failures}
}

/**
 * Renders every clip the plan moves.
 *
 * `clips` rather than the plan alone, because the plan deliberately carries only
 * ids and gains: the caller's own book is the single source of truth about where
 * a clip's bytes live, and a plan is a description of a change, not a place.
 *
 * Only the clips in `plan.changes` are decoded again and rewritten. A page that
 * is already at the book's level keeps the exact bytes it had — which is not only
 * cheaper, it is why re-running the pass is safe: nothing a creator recorded is
 * re-encoded unless it is actually being changed.
 */
export async function applyLevelPlan(
  plan: LevelMatchPlan,
  clips: readonly ReadingClip[],
  onProgress?: LevelProgress,
): Promise<{
  levelled: LevelledReadingClip[]
  failures: ReadingClipFailure[]
}> {
  const byId = new Map(clips.map(clip => [clip.id, clip]))
  const levelled: LevelledReadingClip[] = []
  const failures: ReadingClipFailure[] = []
  const total = plan.changes.length
  let done = 0

  for (const change of plan.changes) {
    const clip = byId.get(change.id)
    if (!clip) {
      done += 1
      onProgress?.(done, total)
      continue
    }
    try {
      const buffer = await decodeAudioForEditing(clipSourceFor(clip.url))
      const rendered = await renderLevelledAudio(buffer, change.gainDb)
      const url = await renderedClipUrl(
        rendered,
        levelKeyFor(clip, change.gainDb),
      )
      levelled.push({clip, url, gainDb: change.gainDb})
    } catch (error) {
      failures.push({clip, problem: audioProblemOf(error)})
    }
    done += 1
    onProgress?.(done, total)
  }

  return {levelled, failures}
}
